import React from 'react';
import {
  Action,
  ActionDropDown,
  Card,
  CardSkeleton,
  HStack, ICON_CHECK, ICON_LIST_BOX_OUTLINE, Message, Select, Stack,
  SimpleTable,
  SimpleTableSkeleton,
} from '@doveit/bricks';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import SimplePageLayout from '../../../layouts/simple-page-layout/SimplePageLayout';
import PaginatedList from '../../../components/paginated-list/PaginatedList';
import usePagination from '../../../hooks/use-pagination/usePagination';
import useTasks from '../../hooks/use-tasks/useTasks';
import ViewSerpTaskCard from '../../containers/view-serp-task-card/ViewSerpTaskCard';
import ViewSerpTaskTableRow from '../../containers/view-serp-task-table-row/ViewSerpTaskTableRow';
import { SortOrder } from '../../../providers/pagination';
import {
  ALL_TASKS_FILTER_OPTION,
  FILTER_OPTIONS, FILTERS_MAPPER, GROUPED_FILTER_OPTIONS, LOAD_SERP_TASKS_ERROR_MESSAGE, NO_SERP_TASKS,
} from './constants';
import useFilters from '../../../hooks/use-filters/useFilters';
import { TaskSerpPageFilterKeys } from './types';

const TaskSerpPage: React.FC = () => {
  const isMobile = useIsDevice('mobile');

  const scrollRef = React.useRef<HTMLDivElement>(null);

  const {
    page,
    paginationQueryParam,
    goToPage,
  } = usePagination('page', scrollRef);

  const {
    filters,
    addFilters,
  } = useFilters(FILTERS_MAPPER);

  const {
    data: tasks,
    isLoading: areTasksLoading,
    error: tasksError,
    mutate: mutateTasks,
  } = useTasks(
    {
      ...(FILTER_OPTIONS.find(({
        key,
      }) => key === filters.filter)?.value),
    },
    {
      page,
      sort: {
        createdAt: SortOrder.DESC,
      },
    },
  );

  const filterLabel = React.useMemo(() => {
    if (filters.filter === ALL_TASKS_FILTER_OPTION.key) {
      return '';
    }

    const filterObject = FILTER_OPTIONS.find((option) => option.key === filters.filter);

    return filterObject?.label.short.toLowerCase();
  }, [filters.filter]);

  const handleTaskUpdate = React.useCallback(async () => {
    await mutateTasks();
  }, [mutateTasks]);

  const handleBadgeClick = React.useCallback((badgeFilter: TaskSerpPageFilterKeys) => () => {
    addFilters({ filter: badgeFilter }, { deleteKeys: [paginationQueryParam] });
  }, [addFilters, paginationQueryParam]);

  return (
    <SimplePageLayout
      toolbar={(
        <HStack
          alignItems="center"
          justifyContent="space-between"
          gap={100}
        >
          <div style={{ flexGrow: '1' }}>
            {!isMobile && (
              <HStack aria-label="Filtri (desktop)" gap={150}>
                {GROUPED_FILTER_OPTIONS.map(({ label, options }) => (
                  <ActionDropDown
                    key={label}
                    label={label}
                    aria-label={`Filtra attività per ${label}`}
                    emphasis="low"
                    color={options.some(({ key }) => key === filters.filter) ? 'primary' : 'neutral'}
                    options={options.map(({ key, label: optionLabel }) => ({
                      key,
                      label: optionLabel.short,
                      color: filters.filter !== key ? 'neutral' : 'primary',
                      iconRight: filters.filter !== key ? undefined : { path: ICON_CHECK },
                      onClick: filters.filter !== key ? handleBadgeClick(key) : undefined,
                    }))}
                  />
                ))}

                <Action
                  label={ALL_TASKS_FILTER_OPTION.label.short}
                  aria-label="Tutte le attività"
                  onClick={filters.filter !== ALL_TASKS_FILTER_OPTION.key
                    ? handleBadgeClick(ALL_TASKS_FILTER_OPTION.key)
                    : undefined}
                  color={filters.filter !== ALL_TASKS_FILTER_OPTION.key
                    ? 'neutral'
                    : 'primary'}
                />
              </HStack>
            )}
            {isMobile && (
              <Select
                value={filters.filter}
                aria-label="Seleziona un filtro dell'attività (mobile)"
                options={FILTER_OPTIONS.map(({ label, key }) => ({
                  label: label.long,
                  value: key,
                }))}
                size="S"
                onChange={({ target }) => handleBadgeClick(target.value as TaskSerpPageFilterKeys)()}
              />
            )}
          </div>
        </HStack>
      )}
    >
      <Card
        paddingSize="M"
        aria-label="Lista attività"
      >
        <Card.Header
          icon={{ path: ICON_LIST_BOX_OUTLINE }}
          title={`Attività${filterLabel ? ` ${filterLabel}` : ''}${(tasks && tasks.totalElements > 0) ? ` (${tasks.totalElements})` : ''}`}
        />
        <Card.Content>
          {areTasksLoading && !isMobile && (
            <SimpleTableSkeleton aria-label="Attività in caricamento" />
          )}
          {areTasksLoading && isMobile && (
            <Stack aria-label="Attività in caricamento">
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </Stack>
          )}
          {tasksError && !areTasksLoading && (
            <Message
              message={LOAD_SERP_TASKS_ERROR_MESSAGE}
              type="critical"
              boxed
            />
          )}
          {(!areTasksLoading && (tasks && tasks.totalElements > 0)) && (
            <PaginatedList
              {...tasks}
              hideResultCount
              goToPage={goToPage}
            >
              {isMobile && tasks.content.map((task) => (
                <ViewSerpTaskCard
                  key={task.id}
                  task={task}
                  onTaskUpdated={handleTaskUpdate}
                />
              ))}
              {!isMobile && (
                <SimpleTable>
                  <SimpleTable.Body>
                    {tasks.content.map((task) => (
                      <SimpleTable.Row
                        asChild
                        key={task.id}
                      >
                        <ViewSerpTaskTableRow
                          task={task}
                          onTaskUpdated={handleTaskUpdate}
                        />
                      </SimpleTable.Row>
                    ))}
                  </SimpleTable.Body>
                </SimpleTable>
              )}
            </PaginatedList>
          )}
          {(tasks && tasks.totalElements === 0) && (
            <Message message={NO_SERP_TASKS} boxed />
          )}
        </Card.Content>
      </Card>
    </SimplePageLayout>
  );
};

export default TaskSerpPage;
